import { PostgrestResponse } from "@supabase/supabase-js";
import { IProdutos } from "../../../types/tables/IProdutos";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

export class ProdutosService extends BaseMultiTenantService<IProdutos> {
  constructor() {
    super(
      "produtos",
      ["descricao", "descricao_reduzida", "ncm"],
      "fk_id_organizacao",
    );
  }

  /**
   * Busca todos os produtos ativos (sem deleted_at)
   */
  async getAllProdutos(
    organizationId: number,
  ): Promise<PostgrestResponse<IProdutos[]>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Busca produtos ativos com informações de grupo e subgrupo
   */
  async getAllWithCategories(
    organizationId: number,
  ): Promise<PostgrestResponse<any[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
        *,
        produtos_grupo:fk_id_grupo(
          id,
          descricao
        ),
        produtos_sub_grupo:fk_id_produto_sub_grupo(
          id,
          descricao,
          fk_id_produto_grupo
        )
      `,
      )
      .eq(this.organizationIdField, organizationId)
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Busca produtos ativos por categoria/grupo
   */
  async getByGrupo(
    organizationId: number,
    grupoId: number,
  ): Promise<PostgrestResponse<any[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
        *,
        produtos_grupo:fk_id_grupo(*),
        produtos_sub_grupo:fk_id_produto_sub_grupo(*)
      `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_grupo", grupoId)
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Busca produtos ativos por subgrupo específico
   */
  async getBySubGrupo(
    organizationId: number,
    subGrupoId: number,
  ): Promise<PostgrestResponse<any[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
        *,
        produtos_grupo:fk_id_grupo(*),
        produtos_sub_grupo:fk_id_produto_sub_grupo(*)
      `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_produto_sub_grupo", subGrupoId)
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Busca produtos ativos em estoque com categorias
   */
  async getEmEstoque(
    organizationId: number,
  ): Promise<PostgrestResponse<any[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
        *,
        produtos_grupo:fk_id_grupo(*),
        produtos_sub_grupo:fk_id_produto_sub_grupo(*)
      `,
      )
      .eq(this.organizationIdField, organizationId)
      .eq("controle_estoque", true)
      .gt("quantidade_estoque", 0)
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Busca produtos ativos disponíveis (em estoque ou sem controle) com categorias
   */
  async getDisponiveis(
    organizationId: number,
  ): Promise<PostgrestResponse<any[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
        *,
        produtos_grupo:fk_id_grupo(*),
        produtos_sub_grupo:fk_id_produto_sub_grupo(*)
      `,
      )
      .eq(this.organizationIdField, organizationId)
      .or("controle_estoque.eq.false,quantidade_estoque.gt.0")
      .is("deleted_at", null)
      .order("descricao");
  }

  /**
   * Atualiza estoque do produto
   */
  async atualizarEstoque(
    organizationId: number,
    id: number,
    quantidade: number,
  ): Promise<void> {
    const { error } = await this.client
      .from(this.tableName)
      .update({ quantidade_estoque: quantidade })
      .eq("id", id)
      .eq(this.organizationIdField, organizationId);

    if (error) {
      throw new Error(`Erro ao atualizar estoque: ${error.message}`);
    }
  }
}

export const produtosService = new ProdutosService();
